<script>
  // SFTP tester. Sub-tabs: Buckets (the test endpoints), Activity (the captured
  // uploads + auth attempts) and Firewall (blocked source IPs). The SFTP server
  // fakes storage into the DB; this page is where you set up a login and watch
  // what a client did.
  //
  // This file is the LIST. Editing a bucket -- credentials, keys, allowed source
  // IPs -- is its own page, SftpBucketDetail.svelte at /sftp/{slug}.
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { api } from '../lib/api.js';
  import { ui } from '../lib/ui.svelte.js';
  import { getUser } from '../lib/auth.js';
  import TextField from '../components/TextField.svelte';
  import Select from '../components/Select.svelte';
  import SftpCaptureRow from '../components/SftpCaptureRow.svelte';
  import SftpKeygenDialog from '../components/SftpKeygenDialog.svelte';

  const me = getUser();
  const isAdmin = me?.portal_role === 'admin';

  const tabs = [
    { id: 'buckets',       label: 'Buckets',  icon: 'folder_shared' },
    { id: 'activity',      label: 'Activity', icon: 'receipt_long' },
    { id: 'sftp-firewall', label: 'Firewall', icon: 'security' },
  ];
  function seedTab() {
    if (typeof window === 'undefined') return 'buckets';
    const t = new URLSearchParams(window.location.search).get('tab');
    return tabs.some((x) => x.id === t) ? t : 'buckets';
  }
  let activeTab = $state(seedTab());
  function setActive(id) {
    activeTab = id;
    const u = new URLSearchParams(window.location.search);
    if (id === 'buckets') u.delete('tab'); else u.set('tab', id);
    const qs = u.toString();
    history.replaceState(null, '', '/sftp' + (qs ? '?' + qs : ''));
    if (id === 'activity') loadCaptures();
    if (id === 'sftp-firewall') loadFirewall();
  }

  let meta = $state({ port: 2222, retention_default: 7, retention_min: 1, retention_max: 30, enabled: false });
  let buckets = $state([]);
  let loading = $state(true);
  let err = $state('');
  // Host shown in the connection string. Locally this is the page host; in
  // production the SFTP port has its own DNS (see docs) -- shown as a hint.
  const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

  async function loadMeta() {
    try { meta = { ...meta, ...(await api.sftpMeta()) }; } catch (_) {}
  }
  async function loadBuckets() {
    loading = true; err = '';
    try { buckets = (await api.listSftpBuckets()).buckets || []; }
    catch (e) { err = e.message; }
    finally { loading = false; }
  }

  onMount(async () => {
    await loadMeta(); await loadBuckets();
    if (activeTab === 'activity') loadCaptures();
    if (activeTab === 'sftp-firewall') loadFirewall();
  });

  function connString(username) {
    return `sftp -P ${meta.port} ${username}@${host}`;
  }
  // The count pill's tooltip is the only place a bucket's list is visible without
  // opening the editor, so it names each entry: "1.2.3.4 — NJ office", one per
  // line. Unlabelled entries show the bare address rather than a trailing dash,
  // and a legacy bare string is tolerated the same way the editor page tolerates
  // it when it loads a bucket.
  function allowedIpTooltip(entries) {
    const lines = (entries || []).map((e) => {
      const ip = typeof e === 'string' ? e : e.ip || '';
      const label = typeof e === 'string' ? '' : (e.label || '').trim();
      return label ? `${ip} — ${label}` : ip;
    });
    return `Only these sources can log in:\n${lines.join('\n')}`;
  }
  function copy(text, label = 'Copied') {
    navigator.clipboard?.writeText(text);
    ui.snack(label, 'success', 1500);
  }

  // ---- Bucket create/edit --------------------------------------------
  // The editor is its own page (/sftp/new to create, /sftp/{slug} to edit). It
  // was a dialog and outgrew one: a bucket carries N credentials, each with N
  // authorized keys, plus an allowed-IP list, and none of that fits a dialog on
  // a phone. The nav state tells that page a plain history.back() lands here,
  // so leaving it does not push a second /sftp entry to press through.
  function openCreate() { navigate('/sftp/new', { state: { sftpFromList: true } }); }
  function openEdit(b) { navigate(`/sftp/${b.id}`, { state: { sftpFromList: true } }); }

  async function removeBucket(b) {
    const ok = await ui.ask({
      title: 'Delete SFTP bucket?',
      message: `Delete ${b.name} and all of its captured activity?`,
      confirmText: 'Delete', destructive: true,
    });
    if (!ok) return;
    try { await api.deleteSftpBucket(b.id); ui.snack('Bucket deleted', 'success'); loadBuckets(); }
    catch (e) { ui.snack(e.message, 'error'); }
  }

  // ---- Generate-key dialog -------------------------------------------
  // Saved credentials only: the key is stored on the bucket the moment it is
  // generated, so the list reloads to show it. (The editor page opens the same
  // dialog against the stateless endpoint for a credential that isn't saved
  // yet.) The dialog owns the type/size/comment form and the one-time private
  // key reveal; all this side supplies is what "generate" means here.
  let genFor = $state(null); // { bucketId, username } | null
  function generateStored(bucketId, username) {
    return async (type, bits, comment) => {
      const r = await api.generateSftpKey(bucketId, username, type, bits, comment);
      loadBuckets();
      return r;
    };
  }

  // ---- Activity (captures) -------------------------------------------
  let captures = $state([]);
  let capTotal = $state(0);
  let capLoading = $state(false);
  let capErr = $state('');
  let fBucket = $state('');
  let fKind = $state('');
  let fFilename = $state('');
  let fUsername = $state('');
  let fIp = $state('');
  let capCtl = null;

  // Bulk selection (Activity tab) -- capture ids currently checked.
  let selectedCaps = $state(new Set());
  let lastSelIndex = -1; // anchor row for shift-click range select
  function selectCap(index, shiftKey) {
    const next = new Set(selectedCaps);
    if (shiftKey && lastSelIndex >= 0) {
      // shift-click: add every row between the anchor and this one
      const lo = Math.min(lastSelIndex, index), hi = Math.max(lastSelIndex, index);
      for (let i = lo; i <= hi; i++) next.add(captures[i].id);
    } else {
      const id = captures[index].id;
      if (next.has(id)) next.delete(id); else next.add(id);
    }
    selectedCaps = next;
    lastSelIndex = index;
  }
  const allCapsSelected = $derived(captures.length > 0 && captures.every((c) => selectedCaps.has(c.id)));
  function toggleAllCaps() {
    selectedCaps = allCapsSelected ? new Set() : new Set(captures.map((c) => c.id));
  }
  async function deleteSelectedCaps() {
    const ids = [...selectedCaps];
    if (ids.length === 0) return;
    const ok = await ui.ask({
      title: `Delete ${ids.length} ${ids.length === 1 ? 'entry' : 'entries'}?`,
      message: 'The selected captures will be permanently removed.',
      confirmText: 'Delete', destructive: true,
    });
    if (!ok) return;
    try {
      await api.deleteSftpCapturesByIds(ids);
      ui.snack(`Deleted ${ids.length}`, 'success');
      selectedCaps = new Set();
      loadCaptures();
    } catch (e) { ui.snack(e.message, 'error'); }
  }

  const bucketFilterOptions = $derived([
    { value: '',       label: 'All buckets' },
    { value: 'global', label: 'Unmatched (unknown username)' },
    ...buckets.map((b) => ({ value: b.id, label: b.name })),
  ]);
  const kindOptions = [
    { value: '',       label: 'All' },
    { value: 'upload', label: 'Uploads' },
    { value: 'auth',   label: 'Auth attempts' },
  ];
  const capActiveCount = $derived(
    [fBucket, fKind, fFilename, fUsername, fIp].filter((v) => v !== '' && v != null).length,
  );

  function resetCaptureFilters() {
    fBucket = ''; fKind = ''; fFilename = ''; fUsername = ''; fIp = '';
    loadCaptures();
  }

  async function loadCaptures() {
    capCtl?.abort();
    capCtl = new AbortController();
    capLoading = true; capErr = '';
    try {
      const r = await api.listSftpCaptures(
        { bucket_id: fBucket, kind: fKind, filename: fFilename, username: fUsername, ip: fIp, limit: 200 },
        { signal: capCtl.signal },
      );
      captures = r.captures || [];
      capTotal = r.total || 0;
      selectedCaps = new Set(); // selection is per result-set
      lastSelIndex = -1;
    } catch (e) {
      if (e?.name === 'AbortError') return;
      capErr = e.message;
    } finally { capLoading = false; }
  }

  function onCaptureDeleted(id) {
    captures = captures.filter((c) => c.id !== id);
    capTotal = Math.max(0, capTotal - 1);
    if (selectedCaps.has(id)) { const n = new Set(selectedCaps); n.delete(id); selectedCaps = n; }
  }

  // ---- Firewall (blocked source IPs) ---------------------------------
  let blockedIps = $state([]);
  let fwThreshold = $state(0);
  let fwLoading = $state(false);
  let fwErr = $state('');
  let newBlockIp = $state('');

  async function loadFirewall() {
    fwLoading = true; fwErr = '';
    try {
      const r = await api.listSftpFirewall();
      blockedIps = r.blocked || [];
      fwThreshold = r.threshold || 0;
    } catch (e) { fwErr = e.message; }
    finally { fwLoading = false; }
  }
  async function blockIp() {
    const ip = newBlockIp.trim();
    if (!ip) return;
    try {
      await api.blockSftpIp(ip);
      ui.snack(`Blocked ${ip}`, 'success');
      newBlockIp = '';
      loadFirewall();
    } catch (e) { ui.snack(e.message, 'error'); }
  }
  async function unblockIp(ip) {
    const ok = await ui.ask({ title: 'Unblock IP?', message: `Allow ${ip} to connect to SFTP again?`, confirmText: 'Unblock' });
    if (!ok) return;
    try { await api.unblockSftpIp(ip); ui.snack(`Unblocked ${ip}`, 'success'); loadFirewall(); }
    catch (e) { ui.snack(e.message, 'error'); }
  }
</script>

<div class="settings-tabs" role="tablist">
  {#each tabs as t}
    <button role="tab" aria-selected={activeTab === t.id} class="settings-tab" class:settings-tab-active={activeTab === t.id} onclick={() => setActive(t.id)}>
      <span class="material-symbols-outlined text-base">{t.icon}</span>
      {t.label}
    </button>
  {/each}
</div>

{#if !meta.enabled}
  <div class="sftp-note">
    <span class="material-symbols-outlined text-base align-middle">info</span>
    The SFTP server is not enabled on this deployment (<code>SFTP_ENABLED=false</code>). You can still configure buckets; they take effect once it's turned on.
  </div>
{/if}

{#if activeTab === 'buckets'}
  {#if isAdmin}
    <div class="flex justify-end mb-4">
      <button onclick={openCreate} class="shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary text-on-primary text-sm font-medium px-3 sm:px-5 py-2.5 sm:py-2" title="Add SFTP bucket">
        <span class="material-symbols-outlined text-base">create_new_folder</span>
        <span class="hidden sm:inline">Add bucket</span>
      </button>
    </div>
  {/if}

  {#if loading}
    <p class="text-on-surface-variant">Loading…</p>
  {:else if err}
    <p class="text-error">{err}</p>
  {:else if buckets.length === 0}
    <div class="bg-surface rounded-2xl p-12 text-center text-on-surface-variant">
      <span class="material-symbols-outlined text-5xl mb-2">folder_shared</span>
      <p>No SFTP buckets yet.</p>
      {#if isAdmin}<p class="text-sm mt-2">Click <em>Add bucket</em> to create a test SFTP endpoint.</p>{/if}
    </div>
  {:else}
    <ul class="sftp-grid">
      {#each buckets as b (b.id)}
        <li class="sftp-card" class:disabled={!b.enabled}>
          <header class="sftp-card-head">
            <span class="material-symbols-outlined text-primary">folder_shared</span>
            <h3 class="sftp-card-name">{b.name}</h3>
            {#if !b.enabled}<span class="sftp-pill">paused</span>{/if}
            {#if (b.allowed_ips || []).length}
              <span class="sftp-pill" title={allowedIpTooltip(b.allowed_ips)}>
                {b.allowed_ips.length} allowed IP{b.allowed_ips.length === 1 ? '' : 's'}
              </span>
            {/if}
            <span class="sftp-pill">{b.retention_days}d TTL</span>
            {#if isAdmin}
              <div class="sftp-card-actions">
                <button onclick={() => openEdit(b)} class="sftp-icon-btn" title="Edit"><span class="material-symbols-outlined">edit</span></button>
                <button onclick={() => removeBucket(b)} class="sftp-icon-btn" title="Delete"><span class="material-symbols-outlined">delete</span></button>
              </div>
            {/if}
          </header>
          <div class="sftp-slug">slug: <span class="font-mono">{b.id}</span></div>

          {#if (b.credentials || []).length === 0}
            <p class="text-xs text-on-surface-variant">No credentials yet — edit to add a username.</p>
          {:else}
            <div class="sftp-creds">
              {#each b.credentials as c}
                <div class="sftp-cred">
                  <div class="sftp-cred-top">
                    <span class="material-symbols-outlined text-base">person</span>
                    <span class="font-medium">{c.username}</span>
                    {#if c.password}<span class="sftp-pill">password</span>{/if}
                    {#if (c.authorized_keys || []).length}<span class="sftp-pill">{c.authorized_keys.length} key{c.authorized_keys.length === 1 ? '' : 's'}</span>{/if}
                  </div>
                  <button class="sftp-conn" onclick={() => copy(connString(c.username), 'Connection string copied')} title="Copy connection string">
                    <span class="material-symbols-outlined" style="font-size:14px;">terminal</span>
                    <span class="sftp-conn-text">{connString(c.username)}</span>
                    <span class="material-symbols-outlined" style="font-size:14px;">content_copy</span>
                  </button>
                  {#if (c.authorized_keys || []).length}
                    <ul class="sftp-keys">
                      {#each c.authorized_keys as k}
                        <li>
                          <button class="sftp-key-copy" onclick={() => copy(k.public_key, `${k.label || k.type} public key copied`)} title="Copy public key">
                            <!-- The label cell is always rendered, empty when a key
                                 has none, so every row's type and fingerprint line
                                 up in the same columns instead of sliding left. -->
                            <span class="sftp-key-label" class:is-empty={!k.label}>{k.label ?? ''}</span>
                            <span class="sftp-key-type font-mono">{k.type}</span>
                            <span class="sftp-key-fp text-on-surface-variant">{k.fingerprint}</span>
                            <span class="material-symbols-outlined" style="font-size:14px;">content_copy</span>
                          </button>
                        </li>
                      {/each}
                    </ul>
                  {/if}
                  {#if isAdmin}
                    <button class="sftp-genkey" onclick={() => (genFor = { bucketId: b.id, username: c.username })}>
                      <span class="material-symbols-outlined" style="font-size:16px;">key</span> Generate keypair
                    </button>
                  {/if}
                </div>
              {/each}
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
{:else if activeTab === 'activity'}
  <!-- Activity tab -->
  <section class="bg-surface-container rounded-2xl p-4 mb-4">
    <form onsubmit={(e) => { e.preventDefault(); loadCaptures(); }}>
      <div class="grid grid-cols-1 min-[550px]:grid-cols-2 md:grid-cols-3 gap-4">
        <Select label="Bucket" bind:value={fBucket} options={bucketFilterOptions} />
        <Select label="Kind" bind:value={fKind} options={kindOptions} />
        <TextField label="IP contains" bind:value={fIp} />
        <TextField label="Filename contains" bind:value={fFilename} />
        <TextField label="Username contains" bind:value={fUsername} />
      </div>
      <div class="flex justify-end gap-2 mt-4">
        <button type="button" onclick={resetCaptureFilters} class="px-4 py-2 rounded-full text-sm text-on-surface-variant hover:bg-surface-container-high">
          Reset
        </button>
        <button type="submit" class="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium bg-primary text-on-primary hover:opacity-90">
          <span class="material-symbols-outlined text-base">refresh</span> Apply
        </button>
      </div>
    </form>
  </section>

  {#if capErr}
    <p class="text-error">{capErr}</p>
  {:else if capLoading && captures.length === 0}
    <p class="text-on-surface-variant">Loading…</p>
  {:else if captures.length === 0}
    <div class="bg-surface rounded-2xl p-12 text-center text-on-surface-variant">
      <span class="material-symbols-outlined text-5xl mb-2">receipt_long</span>
      <p>No SFTP activity captured yet.</p>
      <p class="text-sm mt-2">Connect a client and upload a file to see it here.</p>
    </div>
  {:else}
    <div class="sftp-cap-bar">
      {#if isAdmin}
        <label class="sftp-bulk-all">
          <input type="checkbox" class="sftp-check" checked={allCapsSelected} onchange={toggleAllCaps} aria-label="Select all" />
          <span>{selectedCaps.size ? `${selectedCaps.size} selected` : 'Select all'}</span>
        </label>
      {/if}
      <span class="text-sm text-on-surface-variant">{capTotal.toLocaleString()} total</span>
      <span class="grow"></span>
      {#if isAdmin && selectedCaps.size > 0}
        <button type="button" class="sftp-bulk-del" onclick={deleteSelectedCaps}>
          <span class="material-symbols-outlined text-base">delete</span> Delete selected ({selectedCaps.size})
        </button>
      {/if}
    </div>
    <div class="bg-surface rounded-2xl divide-y divide-outline-variant overflow-hidden">
      {#each captures as c, i (c.id)}
        <SftpCaptureRow capture={c} isAdmin={isAdmin} index={i} selected={selectedCaps.has(c.id)} onToggle={isAdmin ? selectCap : null} onDeleted={onCaptureDeleted} />
      {/each}
    </div>
  {/if}
{:else}
  <!-- Firewall tab -->
  <section class="bg-surface-container rounded-2xl p-4 mb-4">
    <p class="text-sm text-on-surface-variant">
      Blocked source IPs are dropped before the SSH handshake.
      {#if fwThreshold > 0}
        An IP is auto-blocked after <strong>{fwThreshold}</strong> failed auth attempts.
      {:else}
        Auto-blocking is disabled (<code>SFTP_BLOCK_THRESHOLD=0</code>).
      {/if}
      Private/internal IPs are never auto-blocked, and blocking only works where the real client IP is visible (see <code>docs/sftp.md</code>).
    </p>
    {#if isAdmin}
      <form class="flex flex-wrap items-end gap-3 mt-4" onsubmit={(e) => { e.preventDefault(); blockIp(); }}>
        <div class="grow min-w-[200px]"><TextField label="IP address to block" bind:value={newBlockIp} /></div>
        <button type="submit" class="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm font-medium bg-primary text-on-primary hover:opacity-90">
          <span class="material-symbols-outlined text-base">block</span> Block IP
        </button>
      </form>
    {/if}
  </section>

  {#if fwErr}
    <p class="text-error">{fwErr}</p>
  {:else if fwLoading && blockedIps.length === 0}
    <p class="text-on-surface-variant">Loading…</p>
  {:else if blockedIps.length === 0}
    <div class="bg-surface rounded-2xl p-12 text-center text-on-surface-variant">
      <span class="material-symbols-outlined text-5xl mb-2">shield</span>
      <p>No IPs are blocked.</p>
    </div>
  {:else}
    <div class="text-sm text-on-surface-variant mb-2">{blockedIps.length} blocked</div>
    <div class="bg-surface rounded-2xl divide-y divide-outline-variant overflow-hidden">
      {#each blockedIps as b (b.ip)}
        <div class="sftp-fw-row">
          <span class="material-symbols-outlined sftp-fw-icon">{b.auto ? 'gpp_bad' : 'block'}</span>
          <div class="flex-1 min-w-0">
            <div class="font-mono text-sm">{b.ip}</div>
            <div class="sftp-fw-meta">
              <span class="sftp-pill">{b.auto ? 'auto' : 'manual'}</span>
              {#if b.reason}<span>{b.reason}</span>{/if}
              <span>{b.added_at ? new Date(b.added_at).toLocaleString() : ''}</span>
              {#if b.added_by}<span>by {b.added_by}</span>{/if}
            </div>
          </div>
          {#if isAdmin}
            <button type="button" class="sftp-fw-unblock" onclick={() => unblockIp(b.ip)} title="Unblock">
              <span class="material-symbols-outlined text-base">delete</span> Unblock
            </button>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
{/if}

<!-- Generate-key dialog, for a credential that is already saved on a bucket:
     the key is stored the moment it is generated. The bucket editor lives at
     /sftp/{slug} and opens the same dialog against the stateless endpoint. -->
{#if genFor && isAdmin}
  <SftpKeygenDialog
    subject={genFor.username}
    defaultComment={(genFor.username || '').trim()}
    generate={generateStored(genFor.bucketId, genFor.username)}
    onClose={() => (genFor = null)}
  />
{/if}

<style>
  /* Sub-tabs (shared look with Settings). */
  .settings-tabs { display: flex; gap: 0; margin-block-end: 16px; border-block-end: 1px solid rgb(var(--md-sys-color-outline-variant)); }
  .settings-tab { position: relative; display: inline-flex; align-items: center; justify-content: center; gap: 8px; block-size: 48px; padding-inline: 24px; border: 0; background: transparent; color: rgb(var(--md-sys-color-on-surface-variant)); font-size: 0.875rem; font-weight: 500; cursor: pointer; }
  .settings-tab:hover { background: rgb(var(--md-sys-color-on-surface) / 0.06); color: rgb(var(--md-sys-color-on-surface)); }
  .settings-tab::after { content: ''; position: absolute; inset-block-end: -1px; inset-inline: 12px; block-size: 3px; border-radius: 3px 3px 0 0; background: transparent; }
  .settings-tab-active { color: rgb(var(--md-sys-color-primary)); }
  .settings-tab-active::after { background: rgb(var(--md-sys-color-primary)); }

  .sftp-note { background: rgb(var(--md-sys-color-surface)); border-radius: 12px; padding: 0.75rem 1rem; margin-block-end: 1rem; font-size: 0.85rem; color: rgb(var(--md-sys-color-on-surface-variant)); }
  .sftp-note code { font-family: 'Menlo','Consolas',monospace; }

  .sftp-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 0.75rem; list-style: none; margin: 0; padding: 0; }
  .sftp-card { background: rgb(var(--md-sys-color-surface)); border-radius: 16px; padding: 1rem; display: flex; flex-direction: column; gap: 0.5rem; }
  .sftp-card.disabled { opacity: 0.6; }
  .sftp-card-head { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .sftp-card-name { font-size: 16px; font-weight: 500; margin: 0; flex: 1; min-inline-size: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sftp-pill { font-size: 11px; padding: 2px 8px; border-radius: 999px; background: rgb(var(--md-sys-color-surface-container-high)); color: rgb(var(--md-sys-color-on-surface-variant)); }
  .sftp-card-actions { display: flex; gap: 0.25rem; }
  .sftp-icon-btn { inline-size: 32px; block-size: 32px; display: inline-flex; align-items: center; justify-content: center; background: transparent; color: rgb(var(--md-sys-color-on-surface-variant)); border: 0; border-radius: 50%; cursor: pointer; }
  .sftp-icon-btn:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .sftp-icon-btn .material-symbols-outlined { font-size: 18px; }
  .sftp-slug { font-size: 12px; color: rgb(var(--md-sys-color-on-surface-variant)); }

  .sftp-creds { display: flex; flex-direction: column; gap: 0.5rem; }
  .sftp-cred { background: rgb(var(--md-sys-color-surface-container)); border-radius: 12px; padding: 0.625rem 0.75rem; display: flex; flex-direction: column; gap: 0.375rem; }
  .sftp-cred-top { display: flex; align-items: center; gap: 0.375rem; font-size: 0.9rem; flex-wrap: wrap; }
  .sftp-conn { display: inline-flex; align-items: center; gap: 0.375rem; font-family: 'Menlo','Consolas',monospace; font-size: 12px; color: rgb(var(--md-sys-color-primary)); background: transparent; border: 0; cursor: pointer; text-align: start; inline-size: fit-content; max-inline-size: 100%; }
  .sftp-conn-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  /* Grid + subgrid, not flex: with flex each key row sized itself independently,
     so the type wrapped onto a second line on a phone and the fingerprints and
     copy icons landed at different offsets on every row. Four explicit tracks
     (label / type / fingerprint / copy) shared down the chain make every row
     align on the same columns however many keys a credential has. */
  .sftp-keys { display: grid; grid-template-columns: auto auto minmax(0, 1fr) auto; align-items: center; row-gap: 2px; margin: 0; padding: 0; list-style: none; font-size: 11px; }
  .sftp-keys li { display: grid; grid-template-columns: subgrid; grid-column: 1 / -1; min-inline-size: 0; }
  .sftp-key-copy { display: grid; grid-template-columns: subgrid; grid-column: 1 / -1; align-items: center; gap: 0.375rem; padding: 2px 6px; margin-inline-start: -6px; border: 0; border-radius: 6px; background: transparent; color: inherit; font-size: 11px; cursor: pointer; text-align: start; }
  .sftp-key-copy:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .sftp-key-copy .material-symbols-outlined { color: rgb(var(--md-sys-color-primary)); flex: none; }
  .sftp-key-type { white-space: nowrap; }
  .sftp-key-fp { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-inline-size: 0; }
  /* Operator tag, shown ahead of the key type so a key is identifiable at a
     glance without reading its fingerprint. */
  .sftp-key-label { flex-shrink: 0; max-inline-size: 12rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding: 1px 8px; border-radius: 999px; background: rgb(var(--md-sys-color-primary) / 0.14); color: rgb(var(--md-sys-color-primary)); font-size: 11px; font-weight: 600; }
  .sftp-key-label.is-empty { padding: 0; background: none; }
  .sftp-genkey { align-self: flex-start; display: inline-flex; align-items: center; gap: 0.25rem; font-size: 12px; font-weight: 500; color: rgb(var(--md-sys-color-primary)); background: transparent; border: 1px solid rgb(var(--md-sys-color-outline-variant)); border-radius: 999px; padding: 0.25rem 0.75rem; cursor: pointer; }
  .sftp-genkey:hover { background: rgb(var(--md-sys-color-primary) / 0.08); }

  /* Activity + Firewall tabs. */
  .sftp-cap-bar { display: flex; align-items: center; gap: 0.75rem; margin-block-end: 0.5rem; min-block-size: 36px; }
  .sftp-bulk-all { display: inline-flex; align-items: center; gap: 0.5rem; font-size: 13px; color: rgb(var(--md-sys-color-on-surface-variant)); cursor: pointer; }
  .sftp-check { inline-size: 16px; block-size: 16px; accent-color: rgb(var(--md-sys-color-primary)); cursor: pointer; }
  .sftp-bulk-del { display: inline-flex; align-items: center; gap: 0.375rem; height: 36px; padding: 0 16px; border: 0; border-radius: 18px; font-size: 13px; font-weight: 500; background: rgb(var(--md-sys-color-error-container)); color: rgb(var(--md-sys-color-on-error-container)); cursor: pointer; }
  .sftp-bulk-del:hover { opacity: 0.9; }
  .sftp-fw-row { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem 0.875rem; }
  .sftp-fw-icon { color: rgb(var(--md-sys-color-error)); }
  .sftp-fw-meta { font-size: 12px; color: rgb(var(--md-sys-color-on-surface-variant)); margin-top: 2px; display: flex; flex-wrap: wrap; gap: 0 0.75rem; align-items: center; }
  .sftp-fw-unblock { display: inline-flex; align-items: center; gap: 0.375rem; block-size: 34px; padding: 0 14px; border: 1px solid rgb(var(--md-sys-color-outline-variant)); border-radius: 17px; background: transparent; color: rgb(var(--md-sys-color-on-surface-variant)); font-size: 13px; cursor: pointer; }
  .sftp-fw-unblock:hover { background: rgb(var(--md-sys-color-error) / 0.1); color: rgb(var(--md-sys-color-error)); border-color: transparent; }
</style>
